///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthNodeFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { DuplicateTypeEnum } from '../duplicate-type-enum';
import { NodeFeature } from '../../../../logical-topology/entities/node/node-feature';

export class GtthDuplicatedNodeFeatureDto extends GtthNodeFeatureDtoModel {
    public originId: number;
    public useAsBaseConfig: boolean;
    public relatedOrigin: NodeFeature;

    private readonly initialName: string;

    private type: DuplicateTypeEnum;
    private distance: number;

    constructor(node: GtthNodeFeatureDtoModel, relatedTopologyNode: NodeFeature) {
        super();
        this.setId(node.getId());
        this.setName(node.getName());
        this.setLat(node.getLat());
        this.setLng(node.getLng());
        this.setHeight(node.getHeight());
        this.setAltMode(node.getAltMode());
        this.setMhType(node.getMhType());
        this.setLayerIndex(node.getLayerIndex());
        this.setBuDeviceAzimuth(node.getBuDeviceAzimuth());
        this.setToRemove(node.getToRemove());
        this.setIsPop(node.getIsPop());
        this.setIsEnabled(node.getIsEnabled());
        this.setBlockedByPoleAngle(node.getBlockedByPoleAngle());
        this.initialName = node.getName();
        this.relatedOrigin = relatedTopologyNode;
    }

    getOriginId(): number {
        return this.relatedOrigin.getId();
    }

    getOriginName(): string {
        return this.relatedOrigin.getName();
    }

    hasNameEqualsToOrigin(): boolean {
        return this.relatedOrigin.getName() === this.getName();
    }

    setDistance(distance: number) {
        this.distance = distance;
    }

    getInitialName(): string {
        return this.initialName;
    }

    getDistance(): number {
        return this.distance;
    }

    getType(): DuplicateTypeEnum {
        return this.type;
    }

    setNewName(newName: string) {
        this.setName(newName);
    }

    isMarkedAsNew(): boolean {
        return this.getType() === DuplicateTypeEnum.NEW;
    }

    isMarkedAsDoNotImport(): boolean {
        return this.getType() === DuplicateTypeEnum.NOT_IMPORT;
    }

    isMarkedAsUpdateOrigin(): boolean {
        return this.getType() === DuplicateTypeEnum.UPDATE_ORIGIN;
    }

    isMarkedAsPotentialDuplicate(): boolean {
        return this.getType() === DuplicateTypeEnum.POTENTIAL_DUPLICATE;
    }

    switchType(hasUpdateSameOrigin: boolean, hasPotentialDuplicates: boolean) {
        switch (this.type) {
            case DuplicateTypeEnum.NEW:
                if (this.hasNameEqualsToOrigin() && !hasUpdateSameOrigin) {
                    this.setTypeDependOnFreezeState();
                    break;
                }

                if (hasUpdateSameOrigin) {
                    this.setType(DuplicateTypeEnum.NOT_IMPORT);
                } else {
                    this.setTypeDependOnFreezeState();
                }

                if (hasPotentialDuplicates && hasUpdateSameOrigin) {
                    this.setType(DuplicateTypeEnum.POTENTIAL_DUPLICATE);
                } else {
                    this.setTypeDependOnFreezeState();
                }

                break;
            case DuplicateTypeEnum.NOT_IMPORT:
                this.setType(DuplicateTypeEnum.NEW);
                break;
            case DuplicateTypeEnum.UPDATE_ORIGIN:
                if (hasPotentialDuplicates) {
                    this.setType(DuplicateTypeEnum.POTENTIAL_DUPLICATE);
                } else {
                    this.setType(DuplicateTypeEnum.NOT_IMPORT);
                }
                break;
            case DuplicateTypeEnum.POTENTIAL_DUPLICATE:
                this.setType(DuplicateTypeEnum.NOT_IMPORT);
                break;
        }
    }

    setType(type: DuplicateTypeEnum) {
        switch (type) {
            case DuplicateTypeEnum.NEW:
                this.originId = null;
                this.useAsBaseConfig = false;
                break;
            case DuplicateTypeEnum.NOT_IMPORT:
                this.useAsBaseConfig = false;
                this.originId = this.relatedOrigin ? this.getOriginId() : this.getId();
                break;
            case DuplicateTypeEnum.UPDATE_ORIGIN:
            case DuplicateTypeEnum.POTENTIAL_DUPLICATE:
                this.useAsBaseConfig = true;
                this.originId = this.relatedOrigin ? this.getOriginId() : this.getId();
                break;
        }
        this.type = type;
    }

    private setTypeDependOnFreezeState() {
        if (!this.relatedOrigin.hasFreezeLinks()) {
            this.setType(DuplicateTypeEnum.UPDATE_ORIGIN);
        } else {
            this.setType(DuplicateTypeEnum.NOT_IMPORT);
        }
    }
}
